<template>
  <div class="Attract" @click="showHideSelector">

    <messages class="Attract-messages"
      ref="messages"
      v-if="animal.messages"
      :content="animal.messages"
      :alt-colors="selectorIsActive" />

    <transition name="Attract-selector-">

      <div class="Attract-selector"
        v-show="selectorIsActive"
        v-if="animals.length">

        <p class="Attract-selectorMessage">Touch to Select, Swipe for More.</p>

        <carousel class="Attract-selectorCarousel"
          ref="carousel"
          :initial-index="$route.params.animalIndex - 1"
          :next="goNext"
          :prev="goPrev">

          <div class="Attract-selectorCarouselItem"
            v-for="(animal, index) in animals"
            ref="animals"
            @click.stop="openOrSelect(index)">

            <div class="Attract-selectorCarouselItemContent">

              <status-badge class="Attract-selectorCarouselItemStatusBadge"
                v-if="!animal.available" />

              <img class="Attract-selectorCarouselItemImage"
                :src="animal.image">

            </div>

          </div>

        </carousel>

        <transition name="Attract-arrows-">

          <div class="Attract-arrows"
            v-show="!isSliding">

            <icon class="Attract-arrowLeft"
              v-show="showLeftArrow"
              name="arrow-triple-left"
              width="49px"
              height="24.46px" />

            <icon class="Attract-arrowRight"
              v-show="showRightArrow"
              name="arrow-triple-right"
              width="49px"
              height="24.46px" />

          </div>

        </transition>

      </div>

    </transition>

    <transition name="Attract-image-">

      <div class="Attract-image"
        v-show="!selectorIsActive">

        <status-badge class="Attract-statusBadge"
          v-if="!animal.available" />

        <transition name="Attract-imageImage-" mode="in-out">

          <img class="Attract-imageImage"
            v-if="animal.image"
            :src="animal.image"
            :key="animal.id" />

        </transition>

      </div>

    </transition>

  </div>
</template>

<script>
import Carousel from 'components/Carousel'
import Icon from 'components/Icon'
import Messages from 'components/Messages'
import StatusBadge from 'components/StatusBadge'
import polylinearScale from 'polylinear-scale'

// Adapted from Flickty Transformer
// https://github.com/elcontraption/flickity-transformer
const units = {
  perspective: 'px',
  rotate: 'deg',
  rotateX: 'deg',
  rotateY: 'deg',
  rotateZ: 'deg',
  skew: 'deg',
  skewX: 'deg',
  skewY: 'deg',
  translateX: 'px',
  translateY: 'px',
  translateZ: 'px'
}

const transforms = [
  {
    name: 'scale',
    stops: [
      [-540, 0.92],
      [0, 1],
      [540, 0.92]
    ]
  }
]

export default {
  name: 'Attract',

  components: {
    Carousel,
    Icon,
    Messages,
    StatusBadge
  },

  data () {
    return {
      initialized: false,
      selectorIsActive: true, // Must be true to give the carousel time to initialize
      initializeCarousel: false,
      carouselX: 0,
      carouselSelectedIndex: this.$route.params.animalIndex - 1,
      isSliding: false,
      goNext: false,
      goPrev: false,
      cycleCounter: 0,
      idleCycleCounter: 0
    }
  },

  computed: {
    animal () { return this.$store.state.animal },
    animals () { return this.$store.state.animals },
    settings () { return this.$store.state.settings },
    suite () { return this.$store.state.suite },
    lastTouchTimestamp () { return this.$store.state.lastTouchTimestamp },

    showLeftArrow () {
      return this.carouselSelectedIndex > 0
    },

    showRightArrow () {
      return this.carouselSelectedIndex < this.animals.length - 1
    }
  },

  mounted () {
    this.$nextTick(this.init)
  },

  beforeDestroy () {
    this.destroy()
  },

  watch: {
    animals () {
      this.$nextTick(this.init)
    },

    carouselX () {
      this.applyAllTransforms()
    },

    /**
     * When lastTouchTimestamp changes, reset idleCycleCounter
     */
    lastTouchTimestamp () {
      this.idleCycleCounter = 0
    }

    // idleTime () {
    //   this.selectorIsActive = false
    // }
  },

  methods: {
    /**
     * Initialize
     */
    init () {
      if (this.initialized || !this.animals.length) return
      this.initialized = true
      if (this.animals.length === 1) this.selectorIsActive = false
      this.addEventListeners()
      this.createScaleFunctions()
      this.applyAllTransforms()

      // If we've arrived at attract by idling out, deactivate the selector
      // if ((new Date().getTime() - this.idleTime) < 1000) {
      //   this.selectorIsActive = false
      // }
    },

    /**
     * Destroy
     */
    destroy () {
      this.removeEventListeners()
      this.initialized = false
    },

    /**
     * Add event listeners
     */
    addEventListeners () {
      if (!this.$refs.carousel) return
      this.$refs.carousel.$el.addEventListener('initialized', this.handleInitialized)
      this.$refs.carousel.$el.addEventListener('slide', this.handleSlide)
      this.$refs.carousel.$el.addEventListener('pointerStart', this.handlePointerStart)
      this.$refs.carousel.$el.addEventListener('pointerEnd', this.handlePointerEnd)
      this.$refs.carousel.$el.addEventListener('selected', this.handleSelected)
      this.$refs.messages.$el.addEventListener('finished', this.handleMessagesFinished)
    },

    /**
     * Remove event listeners
     */
    removeEventListeners () {
      if (!this.$refs.carousel) return
      this.$refs.carousel.$el.removeEventListener('initialized', this.handleInitialized)
      this.$refs.carousel.$el.removeEventListener('slide', this.handleSlide)
      this.$refs.carousel.$el.removeEventListener('pointerStart', this.handlePointerStart)
      this.$refs.carousel.$el.removeEventListener('pointerEnd', this.handlePointerEnd)
      this.$refs.carousel.$el.removeEventListener('selected', this.handleSelected)
      this.$refs.messages.$el.removeEventListener('finished', this.handleMessagesFinished)
    },

    /**
     * Create scale functions for each transform
     */
    createScaleFunctions () {
      transforms.forEach(transform => {
        const domain = []
        const range = []

        transform.stops.forEach(stop => {
          domain.push(stop[0])
          range.push(stop[1])
        })

        // Create unique scale function
        transform.scale = function (value) {
          return polylinearScale(domain, range, true)(value)
        }
      })
    },

    /**
     * Apply transforms to all animal slides
     */
    applyAllTransforms () {
      this.$refs.animals.forEach((item, i) => {
        this.applyIndividualTransforms(item, i)
      })
    },

    /**
     * Apply transforms to carousel slides
     * @param  {Object} item
     * @param  {Integer} i
     */
    applyIndividualTransforms (item, i) {
      const screenCenter = 540 // 1080 / 2
      const itemCenter = item.offsetLeft + (item.offsetWidth / 2)
      const distFromCenter = Math.abs(screenCenter - this.carouselX - itemCenter)
      const txs = []

      // Make transforms
      transforms.forEach(transform => {
        txs.push(this.makeTransform(transform, distFromCenter))
      })

      // Apply transforms
      item.style.transform = txs.join(' ')
    },

    /**
     * Make an individual transform rule
     * @param  {Object} transform The transform declaration
     * @param  {Number} xPos      Element's proximity to carousel home
     * @return {String}
     */
    makeTransform (transform, xPos) {
      const name = transform.name
      const unit = transform.unit || units[name] || ''
      const tx = transform.scale(xPos)

      return `${name}(${tx}${unit})`
    },

    /**
     * Handle carousel slide event
     */
    handleSlide (event) {
      this.carouselX = event.detail.x
    },

    /**
     * Handle carousel pointerStart event
     */
    handlePointerStart () {
      this.isSliding = true
    },

    /**
     * Handle carousel pointerEnd event
     */
    handlePointerEnd () {
      this.isSliding = false
    },

    /**
     * Handle carousel initialized event
     */
    handleInitialized () {
      this.$nextTick(() => {
        this.selectorIsActive = false
      })
    },

    /**
     * Handle carousel selected event
     * @param  {Object} event
     */
    handleSelected (event) {
      const index = event.detail.index
      this.$store.dispatch('setAnimal', this.animals[index])
      this.$router.push({ params: { animalIndex: index + 1 } })
      this.carouselSelectedIndex = index
    },

    /**
     * Handle messages finished event
     * @param  {Object} event
     *
     * TODO:
     * - When the last animal's messages have finished, trigger data refresh
     */
    handleMessagesFinished (event) {
      const index = this.$route.params.animalIndex - 1
      const nextIndex = index < this.animals.length - 1
        ? index + 1
        : 0

      // console.log(index, nextIndex)

      if (nextIndex === 0) {
        this.cycleCounter++
        this.idleCycleCounter++

        // console.log('cycleCounter: ' + this.cycleCounter)
        // console.log('idleCycleCounter: ' + this.idleCycleCounter)

        // Refresh data every n cycles
        if (this.cycleCounter === 2) {
          this.$store.dispatch('setDataRefreshTimestamp', new Date().getTime())
          // Reset counter
          this.cycleCounter = 0
        }

        // Deactivate selector after `n` loops of inactivity
        if (this.idleCycleCounter === 1) {
          this.idleCycleCounter = 0
          // Wait a moment before deactivating to give the selector a chance to
          // transition back to the first item
          setTimeout(() => {
            this.selectorIsActive = false
          }, 100)
        }
      }

      this.$store.dispatch('setAnimal', this.animals[nextIndex])
      this.$router.push({ params: { animalIndex: nextIndex + 1 } })
    },

    /**
     * Show or hide the selector carousel
     */
    showHideSelector () {
      // If there is only one animal in this suite, go directly to about section
      if (this.animals.length < 2) {
        this.$router.push({ name: 'about' })
        return
      }

      this.selectorIsActive = !this.selectorIsActive
    },

    /**
     * Open application to the about section, or
     * if the item is not the selected item, select it
     */
    openOrSelect (index) {
      if (index > this.carouselSelectedIndex) {
        this.goNext = true
        this.$nextTick(() => { this.goNext = false })
        return
      }

      if (index < this.carouselSelectedIndex) {
        this.goPrev = true
        this.$nextTick(() => { this.goPrev = false })
        return
      }

      this.$router.push({ name: 'about' })
    }
  }
}
</script>

<style>
.Attract {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.Attract-messages {
  position: relative;
  z-index: 10;
  padding: 68px;
}

.Attract-selector {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: 1355px;
}

.Attract-selector--enter-active,
.Attract-selector--leave-active {
  transition: opacity 0.5s ease;
}

.Attract-selector--enter,
.Attract-selector--leave-to {
  opacity: 0;
}

.Attract-selectorMessage {
  margin-top: 60px;
  text-align: center;
  font-size: 26px;
  font-weight: 400;
  color: var(--color-1);
  -webkit-font-smoothing: antialiased;
}

.Attract-selectorCarousel {
  position: absolute;
  z-index: 1;
  right: 0;
  bottom: 110px;
  left: 0;
  width: 1080px;
  height: 1132px;
}

.Attract-selectorCarouselItem {
  padding: 0 40px;
}

.Attract-selectorCarouselItemContent {
  position: relative;
  width: 688px;
  height: 1132px;
  background: var(--color-1);
  border: 2px solid var(--color-7);
  border-radius: 20px;
  overflow: hidden;
}

.Attract-selectorCarouselItemStatusBadge {
  position: absolute;
  z-index: 900;
  right: -32px;
  bottom: 238px;
  width: 303px;
  height: 303px;
  font-size: 26.94px;
  line-height: 37.41px;
  letter-spacing: 0.02em;
  font-weight: 600;
  -webkit-font-smoothing: antialiased;
}

.Attract-selectorCarouselItemImage {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.Attract-arrows {
  position: absolute;
  top: 0;
  right: 116px;
  bottom: 0;
  left: 116px;
  z-index: 0;
}

.Attract-arrows--leave-active {
  transition: opacity 0.5s ease;
}

.Attract-arrows--enter-active {
  transition: opacity 0.5s 1s ease;
}

.Attract-arrows--enter,
.Attract-arrows--leave-to {
  opacity: 0;
}

.Attract-arrowRight,
.Attract-arrowLeft {
  position: absolute;
  top: 50%;
  margin-top: -12px;
}

.Attract-arrowRight {
  right: 0;
}

.Attract-arrowLeft {
  left: 0;
}

.Attract-image {
  position: absolute;
  z-index: 0;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}

.Attract-image--enter-active,
.Attract-image--leave-active {
  transition: all 0.4s ease;
  transform: scale(1);
  transform-origin: 50% 55%;
  will-change: transform, opacity;
}

.Attract-image--enter,
.Attract-image--leave-to {
  opacity: 0;
  transform: scale(0.7);
}

.Attract-statusBadge {
  position: absolute;
  z-index: 900;
  right: -57px;
  bottom: 376px;
  width: 472px;
  height: 472px;
  font-size: 41.95px;
  line-height: 58.26px;
  letter-spacing: 0.02em;
  font-weight: 600;
}

.Attract-imageImage {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: block;
  object-fit: cover;
  width: 100%;
  height: 100%;
}

.Attract-imageImage--enter-active,
.Attract-imageImage--leave-active {
  transition: opacity 1s ease;
}

.Attract-imageImage--enter,
.Attract-imageImage--leave-to {
  opacity: 0;
}
</style>



// WEBPACK FOOTER //
// Attract.vue?6d7c8d0c